import { useEffect, useState } from "react";
import { brandIconLibraryLoaded, loadBrandIconLibrary } from "@/utils/buIcons";
import { logError } from "@/shared/services/ErrorReporting";

/**
 * Whether the on-demand half of the icon library is in memory, fetching it if
 * it is not (NAV-116).
 *
 * The paths live in module state rather than in React, because the answer is the
 * same for the whole tab and outlives any component. So a component needs one
 * piece of state of its own — this — purely to re-render when they arrive.
 *
 * The fetch is shared: every caller awaits the one promise
 * `loadBrandIconLibrary` caches, so a list of twenty units costs one request.
 *
 * `needed` is how a caller says it will draw an icon from that half. An icon in
 * the starter set passes `false` and this fetches nothing, which is the whole
 * point of the split — the common case must not pull a few hundred kilobytes.
 *
 * A failure resolves to `true` rather than retrying. The caller then draws what
 * it can: the starter icons work, and an icon that cannot be fetched is one
 * missing picture beside a name that is still on screen. Reported, so the
 * failure is not silent.
 */
export function useBrandIconLibrary(needed: boolean): boolean {
  const [ready, setReady] = useState(brandIconLibraryLoaded);

  useEffect(() => {
    if (!needed || ready) return;
    let live = true;
    void loadBrandIconLibrary()
      .then(() => {
        if (live) setReady(true);
      })
      .catch((error: unknown) => {
        logError(error as Error);
        if (live) setReady(true);
      });
    return () => {
      live = false;
    };
  }, [needed, ready]);

  return ready;
}
